import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '../../hooks/use-toast';

/**
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH. The redirect_url on the Login page is derived from
 * window.location.origin at click-time; here we only READ the session_id
 * that Emergent Auth appended to our URL fragment and exchange it for a
 * regular JWT via /api/auth/google-callback.
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = location.hash || '';
    const sessionMatch = hash.match(/session_id=([^&]+)/);
    if (!sessionMatch) {
      toast({
        title: 'Sign-in error',
        description: 'Missing session ID from Google callback.',
        variant: 'destructive',
      });
      navigate('/login', { replace: true });
      return;
    }
    const sessionId = decodeURIComponent(sessionMatch[1]);

    (async () => {
      try {
        const res = await fetch('/api/auth/google-callback', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_id: sessionId }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || 'Sign-in failed');

        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        window.dispatchEvent(new Event('tokenChanged'));

        // Wipe fragment so refresh doesn't retry the exchange
        window.history.replaceState(null, '', '/home');
        toast({
          title: 'Signed in with Google',
          description: `Welcome, ${data.user.username}!`,
        });
        navigate('/home', { replace: true });
      } catch (err) {
        toast({
          title: 'Sign-in error',
          description: err.message || 'Could not complete Google sign-in.',
          variant: 'destructive',
        });
        navigate('/login', { replace: true });
      }
    })();
  }, [location.hash, navigate, toast]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      <div className="text-center" data-testid="auth-callback-loading">
        <div className="w-14 h-14 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-cyan-400 font-semibold">Finishing Google sign-in…</p>
      </div>
    </div>
  );
};

export default AuthCallback;
